/**
 * Explore calculations for the Water Chemistry tool (CT-INTERACT, chemistry/CLAUDE.md
 * "Interactive calculation standard"). Pure functions only: the page passes the learner's inputs
 * and renders the returned worked steps, so every number on screen comes from here.
 *
 * Values come from the reviewed constants in `waterChemistry.ts` and `waterChemistryQuality.ts`
 * (reviewStatus 'pending-teaching-review'); nothing new is introduced except the comparison limit
 * below, which is a teaching boundary, not a measured value.
 */
import {
  type ExploreChallenge,
  type ModelField,
  type WorkedStep,
  firstModelError,
  formatModelNumber,
  modelFieldError,
} from './modelInputContract';
import { SPECIFIC_HEATS, VAPORISATION_ENTHALPIES } from './waterChemistry';
import {
  MOLAR_MASS_CA,
  MOLAR_MASS_CACO3,
  MOLAR_MASS_MG,
  OXYGEN_SATURATION_MG_L,
  QUALITY_GUIDELINES,
  exceedsGuideline,
  hardnessAsCaCO3,
  hardnessClass,
  oxygenSaturationPercent,
  type HardnessClass,
} from './waterChemistryQuality';
import { HEATING_INPUT_LIMITS, type HeatingSetup } from './waterHeatingCalc';

/**
 * Starting setup for the water heating explorer: 100 g of liquid water at 20 °C. A short heating
 * time keeps every substance in the comparison inside its limit until the learner adds energy.
 */
export const WATER_EXPLORE_HEATING_DEFAULT: HeatingSetup = { massG: 100, startTemperatureC: 20, powerW: 500, timeS: 4 };

// ---------------------------------------------------------------------------------------------
// Specific heat comparison: the same energy into the same mass of each reviewed substance.
// ---------------------------------------------------------------------------------------------

/** Every substance in the comparison starts here; the reviewed c values are near 25 °C. */
export const COMPARISON_START_C = 25;
/**
 * Teaching limit for the comparison. The reviewed c values are near 25 °C, so the comparison stops
 * at a liquid's normal boiling point, and at 100 °C for a solid, instead of extrapolating.
 */
export const COMPARISON_MAX_C = 100;

export interface SpecificHeatComparisonRow {
  id: string;
  name: string;
  /** J g⁻¹ K⁻¹ near 25 °C. */
  specificHeat: number;
  /** Temperature rise, or null when the substance would pass its comparison limit. */
  deltaTK: number | null;
  /** The temperature where the comparison stops for this substance. */
  limitC: number;
  formula: string;
  working: string;
}

export interface SpecificHeatComparison {
  energyJ: number;
  massG: number;
  rows: SpecificHeatComparisonRow[];
  /** Number of substances that would pass their comparison limit. */
  beyondCount: number;
}

const COMPARISON_ENERGY_FIELD = {
  label: 'Energy supplied',
  unit: 'J',
  min: 0,
  max: HEATING_INPUT_LIMITS.powerW.max * HEATING_INPUT_LIMITS.timeS.max,
} as const;

function comparisonLimitC(id: string): number {
  const liquid = VAPORISATION_ENTHALPIES.find((entry) => entry.id === id);
  return liquid ? Math.min(liquid.boilingPointC, COMPARISON_MAX_C) : COMPARISON_MAX_C;
}

/** ΔT = Q ÷ (m × c) for every reviewed substance. Throws on invalid input (fail closed). */
export function specificHeatComparison(energyJ: number, massG: number): SpecificHeatComparison {
  const error = modelFieldError(HEATING_INPUT_LIMITS.massG, massG) ?? modelFieldError(COMPARISON_ENERGY_FIELD, energyJ);
  if (error) throw new Error(error);
  const q = formatModelNumber(energyJ, 0);
  const m = formatModelNumber(massG);
  const rows = SPECIFIC_HEATS.map((entry): SpecificHeatComparisonRow => {
    const rise = energyJ / (massG * entry.value);
    const limitC = comparisonLimitC(entry.id);
    const within = COMPARISON_START_C + rise <= limitC;
    const substituted = `${q} J ÷ (${m} g × ${entry.value} J/(g·°C))`;
    return {
      id: entry.id,
      name: entry.name,
      specificHeat: entry.value,
      deltaTK: within ? rise : null,
      limitC,
      formula: 'ΔT = Q ÷ (m × c)',
      working: within
        ? `${substituted} = ${formatModelNumber(rise)} °C → ${formatModelNumber(COMPARISON_START_C + rise)} °C`
        : `${substituted}: reaches ${formatModelNumber(limitC)} °C before all the energy is used. The comparison stops there.`,
    };
  });
  return { energyJ, massG, rows, beyondCount: rows.filter((row) => row.deltaTK === null).length };
}

// ---------------------------------------------------------------------------------------------
// Water quality: hardness from Ca²⁺ and Mg²⁺, and dissolved-oxygen saturation.
// ---------------------------------------------------------------------------------------------

export interface QualityExploreSetup {
  calciumMgL: number;
  magnesiumMgL: number;
  dissolvedOxygenMgL: number;
  temperatureC: number;
}

export type QualityExploreInput = keyof QualityExploreSetup;

export const QUALITY_EXPLORE_FIELDS: ModelField<QualityExploreInput>[] = [
  { key: 'calciumMgL', label: 'Calcium, Ca²⁺', unit: 'mg/L', min: 0, max: 500, step: 1 },
  { key: 'magnesiumMgL', label: 'Magnesium, Mg²⁺', unit: 'mg/L', min: 0, max: 200, step: 1 },
  { key: 'dissolvedOxygenMgL', label: 'Dissolved oxygen', unit: 'mg/L', min: 0, max: 20, step: 0.1 },
  { key: 'temperatureC', label: 'Water temperature', unit: '°C', min: 0, max: 40, step: 1 },
];

/** Limestone-region supply values: a very hard sample that fails the aesthetic hardness value. */
export const DEFAULT_QUALITY_EXPLORE: QualityExploreSetup = {
  calciumMgL: 64,
  magnesiumMgL: 18,
  dissolvedOxygenMgL: 7.2,
  temperatureC: 25,
};

/** Temperatures with a reviewed oxygen solubility value, in ascending order. */
export const OXYGEN_TABLE_TEMPERATURES_C = Object.keys(OXYGEN_SATURATION_MG_L)
  .map(Number)
  .sort((a, b) => a - b);

export interface QualityExploreResult {
  calciumAsCaCO3: number;
  magnesiumAsCaCO3: number;
  hardness: number;
  hardnessClass: HardnessClass;
  /** True when hardness is above the ADWG aesthetic value. */
  hardnessExceeds: boolean;
  /** Oxygen solubility at the temperature, or null when the table has no reviewed value. */
  oxygenSaturationMgL: number | null;
  oxygenSaturationPercent: number | null;
  steps: WorkedStep[];
  result: string;
  limitNote: string | null;
}

const fmt = (value: number) => formatModelNumber(value, 1);

/** Hardness, its class, the ADWG comparison and DO saturation. Throws on invalid input (fail closed). */
export function calculateQualityExplore(setup: QualityExploreSetup): QualityExploreResult {
  const error = firstModelError(QUALITY_EXPLORE_FIELDS, setup);
  if (error) throw new Error(error);
  const { calciumMgL, magnesiumMgL, dissolvedOxygenMgL, temperatureC } = setup;
  const calciumAsCaCO3 = (calciumMgL * MOLAR_MASS_CACO3) / MOLAR_MASS_CA;
  const magnesiumAsCaCO3 = (magnesiumMgL * MOLAR_MASS_CACO3) / MOLAR_MASS_MG;
  const hardness = hardnessAsCaCO3(calciumMgL, magnesiumMgL);
  const klass = hardnessClass(hardness);
  if (klass === null) throw new Error('Hardness could not be classified.');
  const guideline = QUALITY_GUIDELINES.hardness;
  const hardnessExceeds = exceedsGuideline('hardness', hardness);
  const oxygenSaturationMgL = OXYGEN_SATURATION_MG_L[temperatureC] ?? null;
  const percent = oxygenSaturationPercent(dissolvedOxygenMgL, temperatureC);

  const steps: WorkedStep[] = [
    {
      id: 'calcium',
      title: 'Calcium as CaCO₃',
      formula: 'Ca²⁺ × M(CaCO₃) ÷ M(Ca)',
      working: `${fmt(calciumMgL)} mg/L × ${MOLAR_MASS_CACO3} ÷ ${MOLAR_MASS_CA} = ${fmt(calciumAsCaCO3)} mg/L`,
      status: 'done',
    },
    {
      id: 'magnesium',
      title: 'Magnesium as CaCO₃',
      formula: 'Mg²⁺ × M(CaCO₃) ÷ M(Mg)',
      working: `${fmt(magnesiumMgL)} mg/L × ${MOLAR_MASS_CACO3} ÷ ${MOLAR_MASS_MG} = ${fmt(magnesiumAsCaCO3)} mg/L`,
      status: 'done',
    },
    {
      id: 'hardness',
      title: 'Total hardness',
      formula: 'hardness = Ca as CaCO₃ + Mg as CaCO₃',
      working: `${fmt(calciumAsCaCO3)} + ${fmt(magnesiumAsCaCO3)} = ${fmt(hardness)} mg/L as CaCO₃ → ${klass}`,
      status: 'done',
    },
    {
      id: 'guideline',
      title: 'Compare with the ADWG',
      formula: `hardness ≤ ${guideline.max} mg/L (${guideline.kind})`,
      working: hardnessExceeds
        ? `${fmt(hardness)} > ${guideline.max} mg/L → outside the ${guideline.kind} value. ${guideline.reason}`
        : `${fmt(hardness)} ≤ ${guideline.max} mg/L → meets the ${guideline.kind} value`,
      status: 'done',
    },
    percent === null || oxygenSaturationMgL === null
      ? {
          id: 'oxygen',
          title: 'Dissolved-oxygen saturation',
          formula: '% saturation = DO ÷ DO(saturated at T) × 100',
          working: '',
          status: 'not-reached',
          note: `no reviewed solubility at ${fmt(temperatureC)} °C`,
        }
      : {
          id: 'oxygen',
          title: 'Dissolved-oxygen saturation',
          formula: '% saturation = DO ÷ DO(saturated at T) × 100',
          working: `${fmt(dissolvedOxygenMgL)} ÷ ${oxygenSaturationMgL} × 100 = ${formatModelNumber(percent, 0)} % at ${fmt(temperatureC)} °C`,
          status: 'done',
          note: 'context only, no guideline value',
        },
  ];

  const oxygenPart = percent === null ? 'oxygen saturation not calculated' : `${formatModelNumber(percent, 0)} % oxygen saturation`;
  return {
    calciumAsCaCO3,
    magnesiumAsCaCO3,
    hardness,
    hardnessClass: klass,
    hardnessExceeds,
    oxygenSaturationMgL,
    oxygenSaturationPercent: percent,
    steps,
    result: `Result: ${fmt(hardness)} mg/L as CaCO₃, ${klass} water, ${hardnessExceeds ? 'outside' : 'meets'} the ADWG value · ${oxygenPart}`,
    limitNote:
      percent === null
        ? `Oxygen solubility is reviewed only at ${OXYGEN_TABLE_TEMPERATURES_C.join(', ')} °C. The model does not guess a value at ${fmt(temperatureC)} °C; enter one of those temperatures.`
        : null,
  };
}

// ---------------------------------------------------------------------------------------------
// Teaching goal (CT-INTERACT C7): one learning question and 3 "Try this" tasks per Explore view.
// Each check is pure and reads only the learner's setup and the model result.
// ---------------------------------------------------------------------------------------------

/** Target band for the "10 °C rise" tasks: 10 °C ± 5 %. */
const TARGET_RISE_C = { min: 9.5, max: 10.5 } as const;
const TASK_MASS_G = 100;
const DOUBLE_MASS_G = 200;

function waterRise(comparison: SpecificHeatComparison): number | null {
  return comparison.rows.find((entry) => entry.id === 'water')?.deltaTK ?? null;
}

function riseInTargetBand(comparison: SpecificHeatComparison): boolean {
  const rise = waterRise(comparison);
  return rise !== null && rise >= TARGET_RISE_C.min && rise <= TARGET_RISE_C.max;
}

/** What a Properties task checks: the heating setup and the same-energy comparison for it. */
export interface PropertiesChallengeInput {
  setup: HeatingSetup;
  comparison: SpecificHeatComparison;
}

export const PROPERTIES_QUESTION = 'Why does water warm up so much more slowly than other substances?';

export const PROPERTIES_CHALLENGES: ExploreChallenge<PropertiesChallengeInput>[] = [
  {
    id: 'ten-degrees',
    prompt: 'Warm 100 g of water by 10 °C (between 9.5 and 10.5 °C).',
    hint: 'Change only the heating time. Each gram of water needs 4.18 J for each °C.',
    isMet: ({ setup, comparison }) => setup.massG === TASK_MASS_G && riseInTargetBand(comparison),
    why: 'Q = m × c × ΔT = 100 g × 4.18 J/(g·°C) × 10 °C = 4180 J. Water needs 4.18 J for every gram and every degree, more than any other substance in the list.',
  },
  {
    id: 'double-mass',
    prompt: 'Now use 200 g of water and get the same 10 °C rise.',
    hint: 'Look at the energy the 100 g needed, then change the time.',
    isMet: ({ setup, comparison }) => setup.massG === DOUBLE_MASS_G && riseInTargetBand(comparison),
    why: 'Twice the mass needs twice the energy for the same rise: 200 g × 4.18 × 10 = 8360 J. Temperature is not the same as heat energy: a bigger sample at the same temperature has taken in more energy.',
  },
  {
    id: 'copper-limit',
    prompt: 'Find an energy that takes copper to its 100 °C limit while the water warms by less than 10 °C.',
    hint: 'Copper starts at 25 °C, so it needs a 75 °C rise.',
    isMet: ({ comparison }) => {
      const copper = comparison.rows.find((entry) => entry.id === 'copper');
      const rise = waterRise(comparison);
      return copper !== undefined && copper.deltaTK === null && rise !== null && rise < TARGET_RISE_C.min;
    },
    why: 'The same energy does not give the same temperature rise. Copper needs only 0.385 J/(g·°C), about 11 times less than water, so when copper has risen 75 °C the same mass of water has risen only about 7 °C.',
  },
];

/** What a Quality task checks: the learner's test results and the model result for them. */
export interface QualityChallengeInput {
  setup: QualityExploreSetup;
  result: QualityExploreResult;
}

export const QUALITY_QUESTION = 'Which ions make water hard, and why does warm water hold less oxygen?';

/** Task 3 is about temperature alone, so it keeps the dissolved oxygen at the default reading. */
const FIXED_OXYGEN_MG_L = DEFAULT_QUALITY_EXPLORE.dissolvedOxygenMgL;
const FIXED_MAGNESIUM_MG_L = DEFAULT_QUALITY_EXPLORE.magnesiumMgL;
const SATURATED_ENOUGH_PERCENT = 90;

export const QUALITY_CHALLENGES: ExploreChallenge<QualityChallengeInput>[] = [
  {
    id: 'meet-guideline',
    prompt: `Keep magnesium at ${FIXED_MAGNESIUM_MG_L} mg/L and lower only calcium until the water meets the ADWG hardness value (200 mg/L or less).`,
    hint: 'Each 1 mg/L of Ca²⁺ counts as about 2.5 mg/L of CaCO₃.',
    isMet: ({ setup, result }) => setup.magnesiumMgL === FIXED_MAGNESIUM_MG_L && !result.hardnessExceeds,
    why: 'Hardness is the CaCO₃ equivalent of Ca²⁺ and Mg²⁺ together. The 18 mg/L of Mg²⁺ already gives 74 mg/L as CaCO₃, so calcium must fall to about 50 mg/L (about 126 mg/L as CaCO₃) to reach 200 mg/L or less.',
  },
  {
    id: 'magnesium-counts-more',
    prompt: 'Make magnesium give more hardness than calcium, while using a smaller mass of magnesium than calcium.',
    hint: 'Try about 30 mg/L of calcium and a little less magnesium.',
    isMet: ({ setup, result }) => setup.magnesiumMgL < setup.calciumMgL && result.magnesiumAsCaCO3 > result.calciumAsCaCO3,
    why: 'Equal masses do not give equal hardness. Mg (24.31 g/mol) is lighter than Ca (40.08 g/mol), so each mg holds more ions: 1 mg/L of Mg²⁺ counts as 4.12 mg/L of CaCO₃, but 1 mg/L of Ca²⁺ only as 2.50.',
  },
  {
    id: 'warm-saturation',
    prompt: `Keep dissolved oxygen at ${FIXED_OXYGEN_MG_L} mg/L and change only the temperature until the water is at least ${SATURATED_ENOUGH_PERCENT} % saturated.`,
    hint: 'The table has values at 0, 5, 10, 15, 20, 25 and 30 °C.',
    isMet: ({ setup, result }) =>
      setup.dissolvedOxygenMgL === FIXED_OXYGEN_MG_L &&
      result.oxygenSaturationPercent !== null &&
      result.oxygenSaturationPercent >= SATURATED_ENOUGH_PERCENT,
    why: 'Warm water holds less oxygen: 14.62 mg/L at 0 °C but only 7.56 mg/L at 30 °C. So 7.2 mg/L is 95 % of the maximum at 30 °C but only 49 % at 0 °C. A higher percentage in warm water does not mean more oxygen for fish.',
  },
];
